import React, { useState } from "react";
import "./ProjectList.css";
import { useSelector } from "react-redux";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonModal,
  IonList,
  IonItem,
  IonLabel,
  IonRadioGroup,
  IonRadio,
} from "@ionic/react";
import {
  arrowBackOutline,
  chevronBackOutline,
  funnelOutline,
  closeOutline,
} from "ionicons/icons";
import GridList from "../components/GridList";
import DifficultyIcon from "../components/DifficultyIcon";
import WrappedSpinner from "../components/WrappedSpinner";
import Error from "../components/Error";
import { ProjectInterface, JSONInterface } from "../interfaces/JSONInterface";
import { StateInterface } from "../interfaces/StateInterface";
import { ComponentProps } from "../interfaces/ComponentProps";

//sort options
const sortOptions = [
  {
    name: "Newest",
    value: "newest",
  },
  {
    name: "Oldest",
    value: "oldest",
  },
  {
    name: "Easiest",
    value: "easiest",
  },
  {
    name: "Hardest",
    value: "hardest",
  },
  {
    name: "Name: A - Z",
    value: "nameAZ",
  },
  {
    name: "Name: Z - A",
    value: "nameZA",
  },
];

const ProjectList: React.FC<
  ComponentProps & { fetchProjectsJSON: Function }
> = ({ match, fetchProjectsJSON }) => {
  const json: JSONInterface | null = useSelector(
    (state: StateInterface) => state.data.json
  );
  const loaded: boolean = useSelector(
    (state: StateInterface) => state.data.loaded
  );
  const error: string | null = useSelector(
    (state: StateInterface) => state.data.error
  );
  const numGridCols: number = useSelector(
    (state: StateInterface) => state.layout.numGridCols
  );
  const [showModal, setShowModal] = useState<boolean>(false);
  const [enableSortButton, setEnableSortButton] = useState<boolean>(true);
  const [sortValue, setSortValue] = useState<string>("easiest");

  const sortProjects = (a: ProjectInterface, b: ProjectInterface) => {
    switch (sortValue) {
      case "newest":
        return b.id - a.id;
      case "oldest":
        return a.id - b.id;
      case "easiest":
        return a.difficulty - b.difficulty;
      case "hardest":
        return b.difficulty - a.difficulty;

      case "nameAZ":
        return a.title.localeCompare(b.title);

      case "nameZA":
        return b.title.localeCompare(a.title);
      default:
        return b.id - a.id;
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton routerLink="/projects" routerDirection="back">
              <IonIcon md={arrowBackOutline} ios={chevronBackOutline}></IonIcon>
            </IonButton>
          </IonButtons>
          <IonTitle>{match.params.tier}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => {
                setShowModal(true);
                setEnableSortButton(false);
              }}
              disabled={!enableSortButton}
            >
              <IonIcon icon={funnelOutline}></IonIcon>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonModal
          isOpen={showModal}
          cssClass="list-modal"
          backdropDismiss={false}
          onDidDismiss={() => setEnableSortButton(true)}
        >
          <IonHeader>
            <IonToolbar style={{ padding: "0px" }}>
              <IonTitle>Sort by</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <IonList>
              <IonRadioGroup
                value={sortValue}
                onIonChange={(e: any) => {
                  setSortValue(e.detail.value);
                  setShowModal(false);
                }}
              >
                {sortOptions.map((option) => (
                  <IonItem key={option.name}>
                    <IonLabel>{option.name}</IonLabel>
                    <IonRadio slot="end" value={option.value} />
                  </IonItem>
                ))}
              </IonRadioGroup>
            </IonList>
          </IonContent>
        </IonModal>
        {error ? (
          <Error error={error} onButtonClick={fetchProjectsJSON} />
        ) : loaded ? (
          <GridList
            data={json?.projects
              .filter((project) => {
                return project[match.params.tier];
              })
              .sort(sortProjects)}
            renderItem={(project: ProjectInterface) => (
              <IonCard
                button
                key={project.title}
                className="list-card"
                routerLink={`${match.url}/${project.title}`}
                routerDirection="forward"
              >
                <IonCardHeader className="list-card-header">
                  <div>
                    <DifficultyIcon value={project.difficulty} size={14} />
                    <h6
                      style={{
                        color: project.labelColor
                          ? project.labelColor
                          : "black",
                      }}
                    >
                      {project.label}
                    </h6>
                  </div>
                </IonCardHeader>
                <IonCardContent className="list-card-content">
                  <div id="list-thumbnail-container">
                    <div id="list-thumbnail-element">
                      <img
                        id="list-thumbnail-image"
                        alt={project.title}
                        src={project.thumbnailUrl}
                      />
                    </div>
                  </div>
                  <h6 className="list-text">{project.title}</h6>
                </IonCardContent>
              </IonCard>
            )}
            numCols={numGridCols}
          />
        ) : (
          <WrappedSpinner />
        )}
      </IonContent>
    </IonPage>
  );
};

export default ProjectList;
